import { type FC, type ReactElement } from "react";

import { CardRowDetail, CardRowDetailColumn, CardRowDetailLine } from "widgets";

import styleCard from "../core/cards/styles/Card.module.scss";

import { TooltipController } from "../core/overlays/components/OverlayControllers.js";
import { getHitName } from "@/app-utils/blast.ts";
import { getBitScoreFillColor } from "@/app-utils/color.ts";
import {
  type IPileupHit,
  type IPileupHspMapping,
  type IPileupParams,
} from "@/app-utils/track-shapes/track-pileup-factory.ts";
import { type BlastHit, type BlastHsp } from "@/graphql.ts";
import { CHAR_WIDTH_IN_PIXEL, LANE_PADDING, MIN_SHAPE_WIDTH } from "@/settings/settings.js";

export const hitPileupParams: IPileupParams = {
  featureHeight: 9,
  lanePadding: LANE_PADDING,
  labelHeight: 14,
  minShapeWidth: MIN_SHAPE_WIDTH,
  charWidthInPixel: CHAR_WIDTH_IN_PIXEL,
};

interface IHitHspChainShapeProps {
  hit: IPileupHit;
  onClick: (hit: BlastHit, hsp: BlastHsp) => void;
}

/**
 * A visualization component that draws the shape of a BLAST hit, possibly containing several HSPs.
 *
 * The component is expected to be used within an existing SVG context and does NOT create its own SVG element. The
 * main input data is a pileup hit whose view coordinates and a width have already been computed elsewhere.
 *
 * created on 4/18/2017
 */
const HitHspChainShape: FC<IHitHspChainShapeProps> = (props) => {
  const pileupHit: IPileupHit = props.hit;
  const hit: BlastHit = pileupHit.hit;
  const hitName: string = getHitName(hit);
  const height: number = hitPileupParams.featureHeight;
  const halfHeight: number = height / 2;
  const translateHit = `translate(${pileupHit.x}, ${pileupHit.y})`;
  const textClassName = "label-top";
  const mappingsCount = pileupHit.mappings.length;

  // Create actual shapes and label elements
  const label: ReactElement = (
    <text x={1} y={-hitPileupParams.labelHeight} dy="1em" className={`hit-name ${textClassName}`}>
      {hitName}
    </text>
  );

  const shapes: ReactElement[] = pileupHit.mappings.map((mapping: IPileupHspMapping, index: number) => {
    const shapeId = `hit-shape-${hit.rank}-${index}`;

    const shapeStyle = {
      fill: getBitScoreFillColor(mapping.hsp.score),
    };

    return (
      <TooltipController
        key={`hit-hsp-chain-shape-${index}`}
        title={`Hit ${hit.rank} HSP ${mapping.hsp.rank}`}
        content={
          <div className={styleCard.dialog_content}>
            <CardRowDetail title={"Details"}>
              <CardRowDetailColumn>
                <CardRowDetailLine title={"Score"}>{mapping.hsp.score} bits</CardRowDetailLine>
                <CardRowDetailLine title={"E-Value"}>{mapping.hsp.eValue}</CardRowDetailLine>
              </CardRowDetailColumn>
            </CardRowDetail>
          </div>
        }
      >
        {(tooltipTriggerProps) => (
          <rect
            {...tooltipTriggerProps}
            id={shapeId}
            className="hit-shape"
            transform={`translate(${mapping.x}, 0)`}
            width={mapping.width}
            height={height}
            style={shapeStyle}
            onClick={() => props.onClick(hit, mapping.hsp)}
          />
        )}
      </TooltipController>
    );
  });

  // Draw a connector line that will be partially covered by the mapping shapes
  const connectorLine: ReactElement =
    mappingsCount <= 1 ? null : (
      <line
        x1={pileupHit.mappings[0].x}
        y1={halfHeight}
        x2={pileupHit.mappings[mappingsCount - 1].x - pileupHit.mappings[0].x}
        y2={halfHeight}
        className="hsp-connector"
      />
    );

  return (
    <g className="hit" transform={translateHit}>
      {connectorLine}
      {shapes}
      {label}
    </g>
  );
};

export { HitHspChainShape };
